// Video utility functions for production-ready video loading
import { isMobileDeviceSync } from '@/lib/utils/mobile-detection';

export interface VideoLoadOptions {
  maxRetries?: number;
  retryDelay?: number;
  timeoutMs?: number;
  fallbackUrls?: string[];
  // When true, performs a HEAD/ranged GET probe before setting the video src.
  // Default is false to avoid extra network calls. Enable only for diagnostics.
  useConnectivityProbe?: boolean;
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.mkv'];

/**
 * Determine media type (video/image) from URL or query params.
 */
export function getMediaTypeFromUrl(url?: string): 'video' | 'image' | null {
  if (!url) return null;
  const lower = url.toLowerCase();

  // Check common video extensions embedded anywhere in the URL
  if (VIDEO_EXTENSIONS.some((ext) => lower.includes(ext))) {
    return 'video';
  }

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(url, base);
    const typeParam =
      parsed.searchParams.get('type') ||
      parsed.searchParams.get('fileType') ||
      parsed.searchParams.get('contentType');

    if (typeParam) {
      const normalized = decodeURIComponent(typeParam).toLowerCase();
      if (normalized.includes('video')) return 'video';
      if (normalized.includes('image')) return 'image';
    }
  } catch (_error) {
    // Fall through to null
  }

  return null;
}

export function isVideoSource(url?: string): boolean {
  const result = getMediaTypeFromUrl(url) === 'video';
  // #endregion
  return result;
}

export class VideoLoader {
  private static async testVideoConnection(url: string): Promise<boolean> {
    try {
      // Prefer HEAD; some CDNs/servers may not support it, so fall back to a tiny ranged GET
      const headResponse = await fetch(url, {
        method: 'HEAD',
        signal: AbortSignal.timeout(5000),
      });
      if (headResponse.ok) return true;
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[VIDEO LOADER] HEAD test failed, trying ranged GET...', error);
      }
    }

    try {
      const getResponse = await fetch(url, {
        method: 'GET',
        headers: { Range: 'bytes=0-1' },
        signal: AbortSignal.timeout(5000),
      });
      return getResponse.ok && (getResponse.status === 200 || getResponse.status === 206);
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[VIDEO LOADER] Ranged GET test failed:', error);
      }
      return false;
    }
  }

  static async loadVideoWithRetry(
    videoElement: HTMLVideoElement,
    url: string,
    options: VideoLoadOptions = {}
  ): Promise<boolean> {
    const {
      maxRetries = 3,
      retryDelay = 1000,
      timeoutMs = 10000,
      fallbackUrls = [],
      useConnectivityProbe = false,
    } = options;

    // Normalize URLs - convert /api/v1/media/download to /api/media/download
    const normalizeUrl = (urlToNormalize: string): string => {
      if (urlToNormalize.includes('/api/v1/media/download')) {
        return urlToNormalize.replace('/api/v1/media/download', '/api/media/download');
      }
      return urlToNormalize;
    };

    // Normalize all URLs
    const normalizedUrl = normalizeUrl(url);
    const normalizedFallbacks = fallbackUrls.map(normalizeUrl);

    // Try the main URL first
    const urlsToTry = [normalizedUrl, ...normalizedFallbacks];

    let lastHttpError: { status: number; statusText: string; message: string } | null = null;

    for (const currentUrl of urlsToTry) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Optional connectivity probe (disabled by default to save calls)
          if (useConnectivityProbe) {
            const isConnectable = await this.testVideoConnection(currentUrl);
            if (!isConnectable) {
              console.warn(`[VIDEO LOADER] Connection test failed for ${currentUrl}`);
              if (attempt < maxRetries) {
                await this.delay(retryDelay * attempt);
                continue;
              }
              break; // Try next URL
            }
          }

          // Pre-check URL accessibility before setting video src
          // This helps catch HTTP errors early
          let headCheckFailed = false;
          try {
            const headResponse = await fetch(currentUrl, {
              method: 'HEAD',
              signal: AbortSignal.timeout(5000),
            });
            
            if (!headResponse.ok) {
              const errorText = await headResponse.text().catch(() => '');
              const errorMsg = `HTTP ${headResponse.status} ${headResponse.statusText}${errorText ? `: ${errorText}` : ''}`;
              
              // Store HTTP error for later reporting
              lastHttpError = {
                status: headResponse.status,
                statusText: headResponse.statusText,
                message: errorMsg,
              };
              
              if (process.env.NODE_ENV === 'development') {
                console.warn(`[VIDEO LOADER] Pre-check failed for ${currentUrl}: ${errorMsg}`);
              }
              
              // If it's a 404 or 403, still try to set the video src to let the video element handle it
              // This ensures the video element gets a proper error state
              if (headResponse.status === 404 || headResponse.status === 403) {
                headCheckFailed = true;
                // Continue to set video src anyway - let the video element handle the error
              } else {
                // For other errors, continue to retry
                if (attempt < maxRetries) {
                  await this.delay(retryDelay * attempt);
                  continue;
                }
                headCheckFailed = true;
              }
            }
          } catch (fetchError) {
            // HEAD request failed, but continue to try loading the video anyway
            // Some servers/CDNs don't support HEAD requests
            if (process.env.NODE_ENV === 'development') {
              console.warn(`[VIDEO LOADER] Pre-check HEAD failed for ${currentUrl}, continuing anyway:`, fetchError);
            }
          }

          // Set the source and try to load
          // Even if HEAD check failed, set the src to let the video element handle the error
          videoElement.src = currentUrl;

          // If HEAD check failed with 404/403, use a shorter timeout since we know it will fail
          // This prevents waiting the full timeout for a known failure
          const effectiveTimeout = headCheckFailed && lastHttpError ? Math.min(2000, timeoutMs) : timeoutMs;

          const result = await this.waitForVideoLoad(videoElement, effectiveTimeout);
          
          // If HEAD check failed and video load also failed, log the HTTP error
          if (headCheckFailed && lastHttpError && !result.success) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`[VIDEO LOADER] Video load failed after HEAD check failure for ${currentUrl}:`, {
                httpError: lastHttpError,
                videoError: result.error,
              });
            }
            // Break to try next URL (don't retry 404/403 errors)
            break;
          }
          if (result.success) {
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
              console.log(`[VIDEO LOADER] Successfully loaded ${currentUrl} on attempt ${attempt}`);
            }
            return true;
          } else {
            // Log the error details
            if (process.env.NODE_ENV === 'development') {
              console.warn(`[VIDEO LOADER] Attempt ${attempt} failed for ${currentUrl}: ${result.error || 'Unknown error'}`);
            }
          }
        } catch (error) {
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.warn(`[VIDEO LOADER] Attempt ${attempt} failed for ${currentUrl}:`, error);
          }
        }

        if (attempt < maxRetries) {
          await this.delay(retryDelay * attempt);
        }
      }

      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[VIDEO LOADER] All attempts failed for ${currentUrl}, trying next URL...`);
      }
    }

    // Only log in development - don't spam production console
    if (process.env.NODE_ENV === 'development') {
      console.error(`[VIDEO LOADER] All URLs and attempts failed. Tried URLs: ${urlsToTry.join(', ')}`);
      
      // Include HTTP error if available
      if (lastHttpError) {
        console.error(`[VIDEO LOADER] Last HTTP error:`, lastHttpError);
      }
      
      if (videoElement.error) {
        const errorCodes = {
          MEDIA_ERR_ABORTED: 1,
          MEDIA_ERR_NETWORK: 2,
          MEDIA_ERR_DECODE: 3,
          MEDIA_ERR_SRC_NOT_SUPPORTED: 4,
        };
        console.error(`[VIDEO LOADER] Video element error:`, {
          code: videoElement.error.code,
          message: videoElement.error.message,
          errorCodes,
          networkState: videoElement.networkState,
          readyState: videoElement.readyState,
        });
      } else {
        // Provide more context when no error object is available
        const networkStateNames = {
          0: 'NETWORK_EMPTY',
          1: 'NETWORK_IDLE',
          2: 'NETWORK_LOADING',
          3: 'NETWORK_NO_SOURCE',
        };
        const readyStateNames = {
          0: 'HAVE_NOTHING',
          1: 'HAVE_METADATA',
          2: 'HAVE_CURRENT_DATA',
          3: 'HAVE_FUTURE_DATA',
          4: 'HAVE_ENOUGH_DATA',
        };
        
        const possibleCauses = [
          lastHttpError ? `HTTP ${lastHttpError.status} error from server: ${lastHttpError.message}` : null,
          videoElement.networkState === 2 ? 'Network request was aborted or failed before completion' : null,
          videoElement.readyState === 0 ? 'Video never started loading (no metadata received)' : null,
          !videoElement.currentSrc ? 'No valid source URL was set' : null,
        ].filter(Boolean);
        
        const recommendations = [
          lastHttpError?.status === 404 ? 'Check if the file exists in S3 storage. The file may have been deleted or the key may be incorrect.' : null,
          lastHttpError?.status === 403 ? 'Check S3 permissions. The file may exist but access is denied.' : null,
          lastHttpError?.status === 500 ? 'Server error. Check S3 configuration and network connectivity.' : null,
          !videoElement.currentSrc ? 'Verify the video URL is valid and accessible.' : null,
        ].filter(Boolean);
        
        console.error(`[VIDEO LOADER] No error object available.`, {
          networkState: `${videoElement.networkState} (${networkStateNames[videoElement.networkState as keyof typeof networkStateNames] || 'UNKNOWN'})`,
          readyState: `${videoElement.readyState} (${readyStateNames[videoElement.readyState as keyof typeof readyStateNames] || 'UNKNOWN'})`,
          src: videoElement.src,
          currentSrc: videoElement.currentSrc,
          httpError: lastHttpError,
          possibleCauses,
          recommendations: recommendations.length > 0 ? recommendations : ['Check browser console and network tab for more details'],
        });
      }
    }
    return false;
  }

  private static waitForVideoLoad(video: HTMLVideoElement, timeoutMs: number): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve({ success: false, error: 'Timeout waiting for video to load' });
        }
      }, timeoutMs);

      const onSuccess = () => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve({ success: true });
        }
      };

      const onError = () => {
        if (!resolved) {
          resolved = true;
          const error = video.error;
          let errorMessage = 'Unknown video error';
          
          if (error) {
            // MediaError constants (standard HTML5 video error codes)
            const MEDIA_ERR_ABORTED = 1;
            const MEDIA_ERR_NETWORK = 2;
            const MEDIA_ERR_DECODE = 3;
            const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;
            
            switch (error.code) {
              case MEDIA_ERR_ABORTED:
                errorMessage = 'Video loading aborted';
                break;
              case MEDIA_ERR_NETWORK:
                errorMessage = 'Network error while loading video';
                break;
              case MEDIA_ERR_DECODE:
                errorMessage = 'Video decode error';
                break;
              case MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorMessage = 'Video format not supported or source not found';
                break;
              default:
                errorMessage = `Video error code: ${error.code}`;
            }
            
            // Add more details if available
            if (error.message) {
              errorMessage += ` - ${error.message}`;
            }
          }
          
          cleanup();
          resolve({ success: false, error: errorMessage });
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        video.removeEventListener('canplay', onSuccess);
        video.removeEventListener('loadeddata', onSuccess);
        video.removeEventListener('error', onError);
      };

      video.addEventListener('canplay', onSuccess, { once: true });
      video.addEventListener('loadeddata', onSuccess, { once: true });
      video.addEventListener('error', onError, { once: true });

      video.load();
    });
  }

  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Mobile detection utility - use unified utility from @/lib/utils/mobile-detection
// Re-export for backward compatibility
export { isMobileDeviceSync as isMobileDevice } from '@/lib/utils/mobile-detection';

/**
 * Resolve responsive video URL to the actual URL that should be used
 * On mobile: uses mobile URL if available, otherwise falls back to desktop
 * On desktop: uses desktop URL if available, otherwise falls back to mobile
 */
export function resolveResponsiveUrl(
  videoConfig: { mobile?: string; desktop?: string } | string | undefined,
  isMobile: boolean
): string | undefined {
  if (!videoConfig) return undefined;
  
  // If it's a string, return as-is
  if (typeof videoConfig === 'string') {
    return videoConfig;
  }
  
  // On mobile: prefer mobile URL, fallback to desktop
  if (isMobile) {
    return videoConfig.mobile || videoConfig.desktop;
  }
  
  // On desktop: prefer desktop URL, fallback to mobile
  return videoConfig.desktop || videoConfig.mobile;
}

/**
 * Extract fallback URLs from ResponsiveBackgroundImage
 * Returns URLs ordered by priority (mobile-first on mobile, desktop-first on desktop)
 */
export function extractFallbackUrls(
  videoConfig: { mobile?: string; desktop?: string } | string | undefined,
  isMobile: boolean
): string[] {
  if (!videoConfig) return [];
  
  // If it's a string, return as single URL
  if (typeof videoConfig === 'string') {
    return [videoConfig];
  }
  
  const urls: string[] = [];
  
  // Mobile-first order for mobile devices (lower cost sources first)
  // Desktop-first order for desktop (higher quality first)
  if (isMobile) {
    if (videoConfig.mobile) urls.push(videoConfig.mobile);
    if (videoConfig.desktop) urls.push(videoConfig.desktop);
  } else {
    if (videoConfig.desktop) urls.push(videoConfig.desktop);
    if (videoConfig.mobile) urls.push(videoConfig.mobile);
  }
  
  return urls;
}

/**
 * Direct video URLs as absolute fallback when proxy fails
 * These can be configured per section or use defaults
 */
export const FALLBACK_VIDEO_URLS = {
  desktop: {
    primary: '', // Will be populated from section data or defaults
    backup: '',
  },
  mobile: {
    primary: '',
    backup: '',
  },
} as const;

/**
 * Detect browser support for WebM video format
 * WebM offers better compression than MP4, especially for mobile devices
 */
export function supportsWebM(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }
  
  const video = document.createElement('video');
  return video.canPlayType('video/webm; codecs="vp8, vorbis"') !== '' ||
         video.canPlayType('video/webm; codecs="vp9, opus"') !== '';
}

/**
 * Get preferred video format based on browser support and device type
 * Prefers WebM for mobile devices (better compression) if supported
 * Falls back to MP4 for maximum compatibility
 */
export function getPreferredVideoFormat(
  videoUrl: string | { mobile?: string; desktop?: string } | undefined,
  isMobile: boolean
): string | undefined {
  if (!videoUrl) return undefined;
  
  // If it's a string, return as-is (format selection happens at upload/encoding level)
  if (typeof videoUrl === 'string') {
    return videoUrl;
  }
  
  // For responsive videos, prefer mobile version on mobile devices (usually smaller/optimized)
  // Prefer desktop version on desktop (usually higher quality)
  if (isMobile) {
    return videoUrl.mobile || videoUrl.desktop;
  }
  
  return videoUrl.desktop || videoUrl.mobile;
}

// Production-specific video configuration
export function getProductionVideoConfig() {
  const isProduction = process.env.NODE_ENV === 'production';
  const isMobile = isMobileDeviceSync();
  
  return {
    // Mobile-specific optimizations: faster timeouts, fewer retries
    // Desktop gets more aggressive retry settings for production
    maxRetries: isProduction 
      ? (isMobile ? Math.min(2, 5) : 5) 
      : 3,
    retryDelay: isProduction 
      ? (isMobile ? Math.min(800, 2000) : 2000) 
      : 1000,
    timeoutMs: isProduction 
      ? (isMobile ? Math.min(8000, 15000) : 15000) 
      : 10000,
    
    // Production-optimized video attributes
    // OPTIMIZED: Use 'metadata' by default to reduce bandwidth
    // CRITICAL videos use metadata + programmatic load() for better control
    videoAttributes: {
      preload: 'metadata' as const, // Use metadata to reduce initial bandwidth
      playsInline: true,
      muted: true,
      autoPlay: true,
      controls: false,
      'webkit-playsinline': 'true',
      crossOrigin: 'anonymous' as const,
    }
  };
}
